// MyReelsPanel — owner-facing lifecycle list for a creator's own reels. The
// feed only ever shows live reels, so this is the one place expired and
// archived uploads stay reachable (and restorable).
import React, { useCallback, useEffect, useState } from 'react';
import { B } from '../constants';
import { promos as promosAPI } from '../api';

const EXTEND_DAYS = 30;

function statusOf(promo) {
  if (promo.archived) return { label: 'Archived', color: B.muted };
  if (promo.expired) return { label: 'Expired', color: '#B4551F' };
  return { label: promo.timeLeft || 'Live', color: B.forest };
}

export default function MyReelsPanel() {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setError(null);
    return promosAPI.mine()
      .then(data => setReels(data.promos || []))
      .catch(err => setError(err.message || 'Failed to load your reels'));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load().finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [load]);

  const run = useCallback(async (promo, action) => {
    setBusyId(promo.id);
    try {
      const updated = await action(promo.id);
      setReels(prev => prev.map(r => (r.id === promo.id ? {
        ...r,
        archived: updated.archived,
        expired: new Date(updated.expiresAt) < new Date(),
        timeLeft: updated.timeLeft,
      } : r)));
    } catch (err) {
      setError(err.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  }, []);

  if (loading) {
    return <p style={{ color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>Loading your reels…</p>;
  }

  return (
    <div>
      {error && (
        <p style={{ color: '#B4551F', fontFamily: "'Montserrat',sans-serif", fontSize: 13 }}>{error}</p>
      )}
      {!reels.length && !error && (
        <p style={{ color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>You haven’t posted a reel yet.</p>
      )}
      {reels.map(promo => {
        const status = statusOf(promo);
        const busy = busyId === promo.id;
        return (
          <div
            key={promo.id}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
              borderBottom: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: "'Montserrat',sans-serif", fontWeight: 700, color: B.night,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{promo.title}</div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: status.color }}>
                {status.label} · {promo.views ?? 0} views
              </div>
            </div>
            {promo.archived ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => run(promo, promosAPI.unarchive)}
                style={{
                  background: B.forest, color: '#fff', border: 'none', borderRadius: 20,
                  padding: '6px 14px', cursor: busy ? 'default' : 'pointer', fontWeight: 700,
                  fontFamily: "'Montserrat',sans-serif", fontSize: 12,
                }}
              >Restore</button>
            ) : (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(promo, id => promosAPI.extend(id, EXTEND_DAYS))}
                  style={{
                    background: 'transparent', color: B.forest, border: `1px solid ${B.forest}`,
                    borderRadius: 20, padding: '6px 14px', cursor: busy ? 'default' : 'pointer',
                    fontWeight: 700, fontFamily: "'Montserrat',sans-serif", fontSize: 12,
                  }}
                >{`+${EXTEND_DAYS}d`}</button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(promo, promosAPI.archive)}
                  style={{
                    background: 'transparent', color: B.muted, border: '1px solid rgba(0,0,0,0.15)',
                    borderRadius: 20, padding: '6px 14px', cursor: busy ? 'default' : 'pointer',
                    fontWeight: 700, fontFamily: "'Montserrat',sans-serif", fontSize: 12,
                  }}
                >Archive</button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
